import { LeaderboardAuthService } from './leaderboard_auth_service.js';
import { LeaderboardAuthSession } from './leaderboard_auth_session.js';
import { addCountryScores, addScores, getLeaderboard, setUserSave } from './leaderboard.requests.js';
import { getLeaderboardCurrentUserId } from './leaderboard_player_context.js';
import { GLOBAL_BOARD_ID, countryBoardId, countryCatchUp, playerCountry } from './leaderboard_country.js';
import { PersistentCache } from '../../offline/persistent_cache.js';
import { NetworkManager } from '../../network_manager.js';

const PENDING_SCORE_KEY = 'leaderboard_pending_score_v1';
/** A country add failed after its global add went through: the next catch-up fixes the country board. */
const COUNTRY_DIRTY_KEY = 'leaderboard_country_dirty_v1';
let flush_in_flight: Promise<void> | null = null;
// Bumped whenever the board on the server changes because of this player, so the
// leaderboard screen refetches on the next open instead of waiting out its throttle.
let board_revision = 0;
/** Country whose board was brought up to the global one in this session. */
let country_synced: string | null = null;
let catch_up_in_flight: Promise<void> | null = null;
// Score adds and the country catch-up run one at a time: a catch-up reading the boards between
// a global add and its country add would count those stars twice.
let chain: Promise<void> = Promise.resolve();

function serial( task: () => Promise<void> ): Promise<void>
{
  const run = chain.then( task, task );
  chain = run.catch( () => {} );
  return run;
}

function readPendingScore(): number
{
  const value = PersistentCache.read<number>( PENDING_SCORE_KEY );
  return typeof value === 'number' && Number.isFinite( value ) && value > 0 ? value : 0;
}

function writePendingScore( score: number ): void
{
  if ( score > 0 ) PersistentCache.write( PENDING_SCORE_KEY, score );
  else PersistentCache.remove( PENDING_SCORE_KEY );
}

function isCountryDirty(): boolean
{
  return PersistentCache.read<boolean>( COUNTRY_DIRTY_KEY ) === true;
}

function setCountryDirty( dirty: boolean ): void
{
  if ( dirty ) PersistentCache.write( COUNTRY_DIRTY_KEY, true );
  else PersistentCache.remove( COUNTRY_DIRTY_KEY );
}

/** Brings the player's score on their country board up to the global one (see countryCatchUp). */
async function catchUpCountry( country: string ): Promise<void>
{
  await LeaderboardAuthService.ensureLoggedIn();
  // The global row must exist to compare with.
  await LeaderboardAuthService.registered();
  const me = { userId: LeaderboardAuthSession.getUserId(), deviceId: getLeaderboardCurrentUserId() };
  // limit 1: the server appends the rows around the player to any list.
  const [ global, local ] = await Promise.all( [
    getLeaderboard( GLOBAL_BOARD_ID, 1 ),
    getLeaderboard( countryBoardId( country ), 1 ),
  ] );
  const catch_up = countryCatchUp( global.users, local.users, me );
  if ( !catch_up )
  {
    console.warn( `[LeaderboardSync] Country catch-up skipped: the player is not on ${GLOBAL_BOARD_ID} yet` );
    return;
  }
  if ( catch_up.delta > 0 || catch_up.missing )
  {
    await addCountryScores( catch_up.delta, country );
    board_revision++;
    console.log( `[LeaderboardSync] Country catch-up: +${catch_up.delta} on ${countryBoardId( country )}` );
  }
  country_synced = country;
  setCountryDirty( false );
}

export const LeaderboardSyncService = {
  boardRevision(): number
  {
    return board_revision;
  },

  async submitScore( score: number ): Promise<void>
  {
    if ( score <= 0 ) return;
    writePendingScore( readPendingScore() + score );
    await this.flushPendingScore();
  },

  flushPendingScore(): Promise<void>
  {
    if ( flush_in_flight ) return flush_in_flight;
    flush_in_flight = serial( async () =>
    {
      for ( let pass = 0; pass < 5; pass++ )
      {
        const score = readPendingScore();
        if ( score <= 0 || !NetworkManager.isOnline() ) return;

        try
        {
          await LeaderboardAuthService.ensureLoggedIn();
          await addScores( GLOBAL_BOARD_ID, score );
          writePendingScore( Math.max( 0, readPendingScore() - score ) );
          board_revision++;
          console.log( `[LeaderboardSync] Submitted score=${score}, leaderboardId=${GLOBAL_BOARD_ID}` );
        }
        catch ( e )
        {
          console.warn( `[LeaderboardSync] Score submit failed, ${score} point(s) kept for the next sync`, e );
          return;
        }

        // The same stars on the country board. Its failure does not hold the global queue: the
        // country board is caught up with the global one the next time it is read.
        const country = playerCountry();
        if ( !country ) continue;
        try
        {
          await addCountryScores( score, country );
        }
        catch ( e )
        {
          setCountryDirty( true );
          country_synced = null;
          console.warn( `[LeaderboardSync] Country score submit failed, the country board catches up later`, e );
        }
      }
    } ).finally( () => { flush_in_flight = null; } );
    return flush_in_flight;
  },

  /**
   * Resolves once the player's country board is up to date with the global one in this session
   * (or that failed); never rejects. The country board waits for it before its full list, so the
   * player's own row is on it with the right score.
   */
  countryReady(): Promise<void>
  {
    const country = playerCountry();
    if ( !country || !NetworkManager.isOnline() ) return Promise.resolve();
    if ( country_synced === country && !isCountryDirty() ) return Promise.resolve();
    if ( !catch_up_in_flight )
      catch_up_in_flight = serial( () => catchUpCountry( country ) )
        .catch( e => console.warn( '[LeaderboardSync] Country catch-up failed', e ) )
        .finally( () => { catch_up_in_flight = null; } );
    return catch_up_in_flight;
  },

  async syncProfile( level: number, name: string, avatar: number, facebook_av_url?: string ): Promise<void>
  {
    try
    {
      const just_logged_in = await LeaderboardAuthService.ensureLoggedIn();
      if ( !just_logged_in )
      {
        await setUserSave( level, name, avatar, facebook_av_url );
        board_revision++;
        console.log( `[LeaderboardSync] Profile synced level=${level}, name=${name}, avatar=${avatar}` );
      }
    }
    catch ( e )
    {
      console.warn( '[LeaderboardSync] Profile sync failed', e );
    }
  },
};
